import urlJoin from 'url-join';

import { openAuthPopin } from '../../common/auth.es6';
import { SEARCH_FILTERS_MAX_SELETED_PLACES } from '../../common/constants.es6';
import { isMobile } from '../../common/device';
import * as Form from '../../common/form.es6';
import Ga from '../../common/ga';
import MAEvent from '../../common/ma-event';
import { disablePageScroll, enablePageScroll } from '../../common/navigation';
import {
    addSelectedPlace,
    initializeFilters,
    initializeValidation,
    validateForm,
} from '../../common/search-filters.es6';
import HTTPService from '../../common/services/HTTPService.es6';
import { bindForm } from '../../common/tagging';
import {
    EstateType,
    eventTypeWithEstateType,
    getThorViewEventProps,
    SearchType,
    thorAvItems,
    thorDistributionType,
    thorFormatPlaceName,
    thorPushEvent,
} from '../../common/thor';
import { displayErrorToaster } from '../../common/toaster.es6';
import { formDataToURL, getURLParam } from '../../common/utils';
import Wa from '../../common/wa';

import * as Listing from './listing.es6';

const GA_CATEGORY = 'Annonces';

/**
 * Map the datas from context to be send to thor
 *
 * @param {Object} maContext
 * @param {String} zone
 * @returns
 */
function searchMapThorEvent(formData, maContext, zone) {
    if (!maContext || !formData) return {};

    const { listingsCount, searchFilterTransactionType, searchFilterPlaces } = maContext;
    const sort = {
        'current_price_date:desc': 'date',
        'price:desc': 'price_low_to_high',
        'price:asc': 'price_high_to_low',
        'area:desc': 'surface_largest_to_smallest',
        'area:asc': 'surface_smallest_to_largest',
    };

    const data = {
        av_distribution_type_global: thorDistributionType(searchFilterTransactionType),
        av_category: 'classified',
        av_search: {
            country: 'france',
            results_nb: listingsCount.toString(),
        },
        ...(zone && { av_zone: zone }),
    };

    const concatFields = (baseField, additionnalValue) => {
        return baseField
            ? `${baseField}|${thorFormatPlaceName(additionnalValue)}`
            : thorFormatPlaceName(additionnalValue);
    };

    searchFilterPlaces.forEach(place => {
        data.av_search.location = concatFields(data.av_search.location, place.name_base);
        if (place.geotype_label === 'subregion') {
            data.av_province = concatFields(data.av_province, place.zip);
        } else {
            data.av_city = concatFields(data.av_city, place.name_base);
            data.av_zip_code = concatFields(data.av_zip_code, place.zip);
            data.av_province = concatFields(data.av_province, place.zip.slice(0, 2));
        }
    });

    formData.forEach(item => {
        switch (item.name) {
            case 'sort':
                data.av_search.sorting = sort[item.value];
                break;
            case 'page':
                data.av_search.page_nb = item.value ? item.value : '1';
                break;
            case 'max_price':
                data.av_search.price_max = item.value;
                break;
            case 'min_price':
                data.av_search.price_min = item.value;
                break;
            case 'min_area':
                data.av_search.estate_space_min = item.value;
                break;
            case 'max_area':
                data.av_search.estate_space_max = item.value;
                break;
            case 'room_counts':
                data.av_search.room_nb = data.av_search.room_nb
                    ? `${data.av_search.room_nb}|${item.value}`
                    : item.value;
                break;
            case 'item_types':
                data.av_search.search_type = data.av_search.search_type
                    ? `${data.av_search.search_type}|${SearchType[item.value]}`
                    : SearchType[item.value];
                data.av_estate_type_global = data.av_estate_type_global
                    ? `${data.av_estate_type_global}|${EstateType[item.value]}`
                    : EstateType[item.value];
                break;
            default:
                break;
        }
    });

    return data;
}

const getAvItemsArgs = () => {
    const { listings, searchFilterTransactionType } = MA.Context;
    return {
        listings,
        listName: 'classified_search_results',
        name: 'classified',
        transactionType: searchFilterTransactionType,
    };
};

/**
 * Initialize form to share listing
 * @param  {jQueryNode} $html Form container
 */
function initializeAlertForm($html, searchForm, uaEvent) {
    const $form = $html.find('[data-search-alert-form]');
    const $messageSuccess = $html.find('[data-search-alert-success]');
    const searchFormData = new FormData(searchForm);
    const $searchFormData = $(searchForm).serializeArray();

    function addSearchFormDataToAlertForm(formData) {
        const newFormData = {};
        const itemTypesArray = [];
        const roomCountsArray = [];

        searchFormData.forEach((value, key) => {
            if (key.indexOf('_') === 0) {
                return;
            }

            if (key === 'item_types') {
                itemTypesArray.push({ key: value });
                newFormData.item_types = itemTypesArray;
            } else if (key === 'room_counts') {
                roomCountsArray.push(value);
                newFormData.room_counts = roomCountsArray;
            } else if (key === 'place_ids') {
                const valueArray = value.split(',');
                newFormData.places = valueArray.map(id => ({ id }));
            } else if (key === 'transaction_types') {
                newFormData.transaction_type = { key: value };
            } else if (key === 'min_area' && value !== '') {
                newFormData.area_min = Number(value);
            } else if (key === 'max_area' && value !== '') {
                newFormData.area_max = Number(value);
            } else if (key === 'min_price' && value !== '') {
                newFormData.budget_min = Number(value);
            } else if (key === 'max_price' && value !== '') {
                newFormData.budget_max = Number(value);
            } else if (key in newFormData) {
                newFormData[key] = `${newFormData[key]},${value}`;
            } else {
                newFormData[key] = value;
            }
        });

        formData.forEach((value, key) => {
            newFormData[key] = value;
        });

        return newFormData;
    }

    function addValueToSearchAlertNameInput() {
        const $searchAlertNameDataAttribute = $('[data-search-alert-name]');
        const placeNameShort = MA.Context.searchFilterPlaces[0].name_short;
        const transactionType =
            MA.Context.searchFilterTransactionType === 'TRANSACTION_TYPE.SELL'
                ? 'Acheter'
                : 'Louer';

        $searchAlertNameDataAttribute.val(`${transactionType} à ${placeNameShort}`);
        $searchAlertNameDataAttribute.attr('placeholder', `${transactionType} à ${placeNameShort}`);
    }

    addValueToSearchAlertNameInput();
    Form.bindXHRForm($form, $messageSuccess, undefined, {
        getData: addSearchFormDataToAlertForm,
    });
    bindForm($form, uaEvent);
    $form.one(MAEvent.formSubmitAttempt, () => {
        thorPushEvent(
            eventTypeWithEstateType(
                'lead_classified_alert_submit',
                MA.Context.searchFilterTransactionType,
            ),
            {
                ...searchMapThorEvent(
                    $searchFormData,
                    MA.Context,
                    isMobile() ? 'fixed-cta-mobile-buttons' : 'top',
                ),
                method: 'email',
            },
        );
    });
}

/**
 * Initialize alert popin
 * @param {FormElement} searchForm
 * @param {Element} listContainer List of results
 */
function initializeAlertPopin(searchForm, button, eventLabel) {
    const uaLabelPrefix = MA.Context.listingsCount === 0 ? ':no_result' : '';

    $(button).on('click', function onCreateAlertClick(e) {
        const uaLabel = `from:listings${uaLabelPrefix}:${eventLabel}:popin`;

        // check whether there are any validation errors to prevent openPopin
        if (!validateForm(searchForm)) {
            displayErrorToaster('Votre recherche comporte des erreurs');
            return;
        }

        const openAlertPopin = () => {
            const $this = $(this);

            e.preventDefault();
            $.magnificPopup.open(
                {
                    type: 'ajax',
                    fixedContentPos: 'fixed',
                    items: {
                        src: $this.data('url'),
                    },
                    callbacks: {
                        ajaxContentAdded() {
                            // Ajax content is loaded and appended to DOM
                            disablePageScroll();
                            initializeAlertForm(this.content, searchForm, {
                                category: GA_CATEGORY,
                                actionPrefix: 'listings:create_alert:popin',
                                label: uaLabel,
                            });
                        },
                        close() {
                            enablePageScroll();
                        },
                    },
                },
                0,
            );
        };

        if (!window.MA_USER.authenticated) {
            openAuthPopin(openAlertPopin, uaLabel, 'Annonces');
        } else {
            openAlertPopin();
        }
    });
}

/**
 * Submit the form, call and display results
 * @param {HTMLFormElement} form
 * @param {Element} container List of results
 * @param {Object} extraParams Add params to add in queryString
 * @param {String} customUrl Use a custom URL rather than the form action
 * @return {Promise<Object>}
 */
function submitForm(form, container, extraParams, customUrl) {
    const $form = $(form);
    const submitButton = form.querySelector('[data-submit]');
    let url = formDataToURL(form, extraParams);

    Form.pendingFormUI(true, submitButton, container);

    if (customUrl) {
        url = customUrl;
    }

    // tagging
    $form.trigger(MAEvent.formSubmitAttempt);

    return HTTPService.get(url)
        .then(response => {
            if (response.data.seo_url) {
                url = response.data.seo_url;
            }

            // eslint-disable-next-line no-param-reassign
            container.innerHTML = response.data.html;
            MA.Context.listings = response.data.listings;
            MA.Context.listingsCount = Number(response.headers['x-total-count']);
            window.history.pushState({}, document.title, url);
            Form.pendingFormUI(false, submitButton, container);
            window.scrollTo(0, container.offsetTop - 16);

            // tagging
            $form.trigger(MAEvent.formSubmitSuccess);
            const thorPageTitle = eventTypeWithEstateType(
                'classified_search_results',
                MA.Context.searchFilterTransactionType,
            );
            thorPushEvent('page_view', {
                ...getThorViewEventProps('classified', thorPageTitle),
                ...searchMapThorEvent($form.serializeArray(), MA.Context),
                av_items: thorAvItems(getAvItemsArgs()),
            });

            // and avant premiere popin handler.
            initializeAlertPopin(form, '[data-exclusives-button]', 'avant_premiere');

            return response;
        })
        .catch(error => {
            Form.pendingFormUI(false, submitButton, container);
            $form.trigger(MAEvent.formSubmitError);
            return error;
        });
}

/**
 * Initialize search listing sort
 * @param {FormElement} form
 * @param {Element} listContainer List of results
 */
function initializeSearchListingSort(form, listContainer) {
    $(listContainer).on('change', '[data-search-listing-result-sort]', event => {
        const sortSelectValue = $(event.target).val();
        const sortField = document.querySelector('[data-sort-field]');
        sortField.value = sortSelectValue;
        submitForm(form, listContainer, {
            page: 1,
        });

        Ga.send({
            hitType: 'event',
            eventCategory: GA_CATEGORY,
            eventAction: `listings:listings_list:${sortSelectValue}`,
            eventLabel: 'listings:listings_list:sort_by_list',
        });
    });
}

/**
 * Initialize search listing paginate
 * @param {FormElement} form
 * @param {Element} listContainer List of results
 */
function initializeSearchListingPaginate(form, listContainer) {
    $(listContainer).on(
        'click',
        '[data-paginate-page-num], [data-paginate-next], [data-paginate-previous]',
        event => {
            event.preventDefault();
            const pageValue = $(event.currentTarget).data('paginate-page-num');
            const pageField = document.querySelector('[data-page-field]');
            pageField.value = pageValue;
            submitForm(form, listContainer);
        },
    );
}

/**
 * Generate the DOM for a suggestions list
 * @param {[Object]} places Places GeoAPI representation
 * @return {HTMLElement}
 */
function getSuggestions(places) {
    const suggestions = document.createElement('span');
    for (let i = 0; i < places.length; i++) {
        const a = document.createElement('button');
        const place = places[i];
        a.setAttribute('class', 'btn btn--link');
        a.setAttribute('data-place-nearby-suggestion', '');
        a.addEventListener('click', () => {
            addSelectedPlace({
                ...place,
                name: place.title, // Workaround for GeoAPI response inconsistency
                name_short: place.short_title, // Workaround for GeoAPI response inconsistency
            });
            if (a.previousSibling) {
                a.parentElement.removeChild(a.previousSibling);
            } else if (a.nextSibling) {
                a.parentElement.removeChild(a.nextSibling);
            }
            a.parentElement.removeChild(a);
            Ga.send({
                hitType: 'event',
                eventCategory: GA_CATEGORY,
                eventAction: 'listings:search_form:location_suggested:clicked',
                eventLabel: 'listings:search_form:location_suggested',
            });
        });
        a.innerHTML = place.short_title;
        suggestions.appendChild(a);
        if (i < places.length - 1) {
            suggestions.appendChild(document.createTextNode(', '));
        }
    }
    return suggestions;
}

/**
 * Load nearby places suggestions from GeoAPI
 * @param {Element} container
 * @param {String} placeType Pluralized place type
 * @param {String} placeId
 */
function loadPlaceSuggestions(container, placeType, placeId) {
    HTTPService.getGeo(urlJoin(placeType, placeId, 'nearby'), {
        types: 'cities,arrmuns',
        fields: 'short_title',
        limit: 5,
    }).then(response => {
        const places = response.data.places.place;
        if (!container) {
            return;
        }
        if (places.length === 0) {
            // eslint-disable-next-line no-param-reassign
            container.innerHTML = '';
            return;
        }
        // eslint-disable-next-line no-param-reassign
        container.innerHTML = 'Suggestions : ';
        container.setAttribute('class', 'margin-top-double');
        container.appendChild(getSuggestions(places));
    });
}

/**
 * Handle place field behavior to load suggestion
 * @param {jQuery} $placeField
 */
function initializePlaceFieldSuggestions($placeField) {
    const container = document.querySelector('[data-nearby-places]');
    if (!$placeField || $placeField.length === 0 || !container) {
        return;
    }
    $placeField.on(MAEvent.placeSelected, event => {
        if (event.place) {
            loadPlaceSuggestions(
                container,
                MA.Config.PLACE_TYPE_PLURALS[event.place.geotype_label],
                event.place.id,
            );
        }
    });

    $(document).on(MAEvent.searchFiltersPlaceTagsUpdated, event => {
        const buttons = document.querySelectorAll('[data-place-nearby-suggestion]');
        if (event.selectedPlaces.length < SEARCH_FILTERS_MAX_SELETED_PLACES) {
            buttons.forEach(button => button.removeAttribute('disabled'));
        } else {
            buttons.forEach(button => button.setAttribute('disabled', true));
        }
    });

    if (Array.isArray(MA.Context.searchFilterPlaces) && MA.Context.searchFilterPlaces.length > 0) {
        const placeType =
            MA.Config.PLACE_TYPE_PLURALS[MA.Context.searchFilterPlaces[0].geotype_label];
        const placeId = String(MA.Context.searchFilterPlaces[0].id);
        if (placeType && placeId) {
            loadPlaceSuggestions(container, placeType, String(placeId));
        }
    }
}

/**
 * Handle the submission of the form to call API
 * @param {HTMLFormElement} form
 * @param {HTMLElement} container Container of listings
 * @param {Function} successCallback
 */
function initializeFormSubmit(form) {
    const $form = $(form);

    $form.one('change', () => {
        $form.trigger(MAEvent.formStarted);
    });

    bindForm($form, {
        category: GA_CATEGORY,
        actionPrefix: 'listings:search_form',
        label: 'listings:search_form',
    });

    if (isMobile()) {
        const alertButtons = document.querySelectorAll('[data-alert-button]');
        if (alertButtons) {
            alertButtons.forEach(alertButton => {
                alertButton.addEventListener('click', () => {
                    Ga.send({
                        hitType: 'event',
                        eventCategory: GA_CATEGORY,
                        eventAction: 'listings:modify_search_button:clicked',
                        eventLabel: 'listings:modify_search_button',
                    });
                });
            });
        }
    }
}

/**
 * Display an error toaster message when a listing is expired
 */
function initializeExpiredListingToaster() {
    const warningsURLParam = getURLParam('warnings');
    const errorsURLParam = getURLParam('errors');

    if (warningsURLParam && warningsURLParam[0] === 'warning_listing_expired') {
        displayErrorToaster('Cette annonce est expirée');
    }
    if (errorsURLParam && errorsURLParam[0] === 'sale_preview_complete') {
        displayErrorToaster('Le délai de 3 jours est écoulé, cette Avant-première est terminée.');
    }
}

/**
 * Use pop state event to reload listing list
 * @param {HTMLFormElement} form
 * @param {Element} container List of results
 */
function initializePopStateEvent(form, container) {
    window.onpopstate = () => {
        submitForm(form, container, undefined, document.location.href);
    };
}

function initializeTracking(form) {
    const $form = $(form);
    const alertButtons = document.querySelectorAll('[data-alert-button]');

    const $links = $('[data-search-listing-item]');

    $links.on('click', event => {
        if (event.target.nodeName !== 'A') return;
        const { index } = event.currentTarget.dataset;

        thorPushEvent('select_item', {
            av_estate_type_global: EstateType[MA.Context.listings[index].item_type.key],
            av_distribution_type_global: thorDistributionType(
                MA.Context.searchFilterTransactionType,
            ),
            av_items: thorAvItems(getAvItemsArgs()),
            method: 'email',
        });
    });

    if (alertButtons) {
        alertButtons.forEach(alertButton => {
            alertButton.addEventListener('click', () => {
                thorPushEvent('lead_classified_alert_opened', {
                    ...searchMapThorEvent(
                        $form.serializeArray(),
                        MA.Context,
                        isMobile() ? 'fixed-cta-mobile-buttons' : 'top',
                    ),
                    method: 'email',
                });
            });
        });
    }
}

export function domReady() {
    const $placeField = $('[data-places-search]');
    const $selectedPlaces = $('[data-selected-places]');
    const form = document.querySelector('[data-search-filters-form]');
    const listContainer = document.querySelector('[data-listings-container]');

    const thorPageTitle = eventTypeWithEstateType(
        'classified_search_results',
        MA.Context.searchFilterTransactionType,
    );

    const onFormSubmit = targetForm => submitForm(targetForm, listContainer, { page: 1 });

    initializeFormSubmit(form);

    initializeAlertPopin(form, '[data-alert-button]', 'create_alert');
    initializeAlertPopin(form, '[data-exclusives-button]', 'avant_premiere');

    initializeExpiredListingToaster();
    initializeFilters(form, $placeField, $selectedPlaces, formRef => {
        initializeValidation(formRef, validateForm, onFormSubmit);
    });
    initializePlaceFieldSuggestions($placeField);
    initializePopStateEvent(form, listContainer);
    initializeSearchListingPaginate(form, listContainer);
    initializeSearchListingSort(form, listContainer);
    Listing.bindFollowListingButton('[data-listings-container]');
    thorPushEvent('page_view', {
        ...getThorViewEventProps('classified', thorPageTitle),
        ...searchMapThorEvent($(form).serializeArray(), MA.Context),
        av_items: thorAvItems(getAvItemsArgs()),
    });
    Wa.setTracking();
    initializeTracking(form);
}
